/**
 * Piezas de LA CUENTA (Despacho → cuenta.html, validada 2026-09-16). Cada
 * una envuelve UNA pieza de system.css; el contrato visual es la canónica:
 * misma estructura, mismas clases, mismos estados. Aquí no se decide nada
 * de producto — quién puede qué lo decide la pantalla con §5 en la mano.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cx } from "../cx";
import { Icon, type IconName } from "../icons/Icon";

/* ── la modal de cuenta: velo + capa + caja, con el teclado completo ────── */

const FOCABLES = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Los focables VIVOS de un diálogo. Fuera: los apagados con porqué
 * (`tabindex="-1"`), los de una región oculta o inerte (pestaña cerrada,
 * formulario plegado) y los de un grupo replegado (`visibility:hidden`). Sin
 * este filtro el «último» del ciclo era un mando que nunca recibe foco y el
 * Tab se escapaba del diálogo. La prueba mira el DOM, no la maquetación:
 * `offsetParent` no existe fuera de un navegador de verdad.
 */
export function focables(box: HTMLElement | null): HTMLElement[] {
  return Array.from(box?.querySelectorAll<HTMLElement>(FOCABLES) ?? []).filter(
    (el) => el.tabIndex >= 0 && el.closest("[hidden],[inert]") === null && getComputedStyle(el).visibility !== "hidden",
  );
}

/**
 * Devolver el foco al cerrar. Se hace en el frame SIGUIENTE: cuando el
 * diálogo se cierra navegando, el mando que lo abrió está todavía `inert`
 * detrás en ese instante y el `focus()` no prende — el foco se cae al body.
 * `selector` lo dice la pantalla (el mando de su cabecera), porque cuando el
 * diálogo ES la ruta, al montarse el foco ya se había perdido y no hay
 * «antes» que recordar.
 */
export function devolverFoco(antes: HTMLElement | null, selector?: string): void {
  const poner = (): void => {
    const el = selector === undefined ? antes : document.querySelector<HTMLElement>(selector) ?? antes;
    if (el !== null && el.isConnected) el.focus();
  };
  poner();
  requestAnimationFrame(() => { if (document.activeElement === document.body || document.activeElement === null) poner(); });
}

/**
 * Trampa de foco de un diálogo abierto: el foco entra, Escape cierra, Tab no
 * se escapa a la página de detrás y al cerrar vuelve al mando que lo abrió.
 * `enabled` permite cederle el turno a otro diálogo por encima (la modal
 * destructiva de la zona delicada).
 */
export function useDialogKeys(box: React.RefObject<HTMLElement | null>, open: boolean, onClose: () => void, enabled = true, initial?: string, returnFocus?: string): void {
  const cerrar = useRef(onClose);
  cerrar.current = onClose;
  useEffect(() => {
    if (!open || !enabled) return;
    const antes = document.activeElement as HTMLElement | null;
    // el foco ENTRA al abrir: en el cierre si la pieza lo pide, si no en el
    // primer mando vivo. Un diálogo que abre con el foco fuera deja al
    // teclado en la página de detrás, que ya es inerte.
    if (box.current?.contains(document.activeElement) !== true) {
      const preferido = initial === undefined ? null : box.current?.querySelector<HTMLElement>(initial) ?? null;
      (preferido ?? focables(box.current)[0])?.focus();
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        // dentro de un campo, Escape es del campo (cancelar el texto)
        const t = e.target;
        if (t instanceof Element && t.closest("textarea,input:not([type=checkbox]):not([type=radio])") !== null) return;
        // y con un listbox del sistema abierto, Escape lo cierra a él primero
        // (dos oyentes en `document` no se paran con stopPropagation)
        if (document.querySelector(".sk-select.sk-open") !== null) return;
        e.preventDefault(); cerrar.current(); return;
      }
      if (e.key !== "Tab") return;
      const f = focables(box.current);
      const primero = f[0], ultimo = f[f.length - 1];
      if (primero === undefined || ultimo === undefined) return;
      if (box.current?.contains(document.activeElement) !== true) { e.preventDefault(); primero.focus(); }
      else if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); devolverFoco(antes, returnFocus); };
    // `onClose` NO va en las dependencias a propósito: llega por ref. Si
    // entrara, el efecto se rearmaría en cada render (la pantalla lo pasa
    // como función nueva) y su limpieza devolvería el foco al mando de
    // apertura una y otra vez — el teclado no podía moverse por el diálogo.
  }, [box, open, enabled, initial, returnFocus]);
}

export interface AccountShellProps {
  onClose: () => void;
  /** id del título de la sección abierta — el diálogo se nombra con ella. */
  labelledBy?: string | undefined;
  /** Nombre del diálogo cuando no hay sección que nombrar (cargando). */
  label?: string | undefined;
  /** false mientras la modal destructiva está encima: el turno es de ella. */
  keys?: boolean | undefined;
  /** Selector del mando al que vuelve el foco al cerrar (el de la cabecera). */
  returnFocus?: string | undefined;
  children: ReactNode;
}
/** La modal de la cuenta: velo + capa + caja a dos columnas (nav + cuerpo). */
export function AccountShell({ onClose, labelledBy, label, keys = true, returnFocus, children }: AccountShellProps) {
  const caja = useRef<HTMLDivElement>(null);
  // como la canónica: el foco entra en el cierre — el primer mando de la
  // nav abriría la cuenta con una pestaña «pulsada» sin haberla pulsado
  useDialogKeys(caja, true, onClose, keys, ".sk-account-close", returnFocus);
  return (
    <>
      <div className="sk-scrim sk-open" onClick={onClose} />
      <div className="sk-modal-layer sk-open">
        <div
          ref={caja} className="sk-modal sk-modal--account" role="dialog" aria-modal="true"
          aria-labelledby={labelledBy} aria-label={labelledBy === undefined ? label : undefined}
        >
          {children}
        </div>
      </div>
    </>
  );
}

/** Flanco izquierdo de la modal: quién eres, las secciones y la salida. */
export function AccountNav({ children, ...rest }: HTMLAttributes<HTMLElement>) {
  return <nav className="sk-account-nav" {...rest}>{children}</nav>;
}
export function AccountWho({ initials, name, role, skeleton }: { initials: ReactNode; name: ReactNode; role: ReactNode; skeleton?: boolean | undefined }) {
  return (
    <div className="sk-account-who">
      <span className={cx("sk-avatar", "sk-avatar--l", skeleton === true && "sk-skeleton")} aria-hidden={skeleton === true ? true : undefined}>{skeleton === true ? null : initials}</span>
      <div><b>{name}</b><span>{role}</span></div>
    </div>
  );
}
/**
 * Tablist vertical con foco itinerante (flechas, Inicio y Fin), como la
 * canónica: solo la pestaña abierta está en el orden de tabulación.
 */
export function AccountTabs({ label, tabs, current, onSelect, inert }: {
  label: string;
  tabs: ReadonlyArray<{ id: string; label: string; icon: IconName }>;
  current: string;
  onSelect: (id: string) => void;
  inert?: boolean | undefined;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const i = tabs.findIndex((t) => t.id === current);
  /** Foco itinerante: mueve el foco Y abre, como manda el patrón de tablist. */
  const ir = (n: number): void => {
    const t = tabs[(n + tabs.length) % tabs.length];
    if (t === undefined) return;
    onSelect(t.id);
    caja.current?.querySelector<HTMLElement>(`#t-${t.id}`)?.focus();
  };
  return (
    <div
      ref={caja} className="sk-account-tabs" role="tablist" aria-orientation="vertical" aria-label={label} inert={inert}
      onKeyDown={(e) => {
        if (i < 0) return;
        if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); ir(i + 1); }
        else if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); ir(i - 1); }
        else if (e.key === "Home") { e.preventDefault(); ir(0); }
        else if (e.key === "End") { e.preventDefault(); ir(tabs.length - 1); }
      }}
    >
      {tabs.map((t) => (
        <button
          key={t.id} type="button" className="sk-nav-item" role="tab" id={`t-${t.id}`} aria-controls={`p-${t.id}`}
          aria-selected={t.id === current} tabIndex={t.id === current ? 0 : -1}
          onClick={() => onSelect(t.id)}
        >
          <Icon name={t.icon} />{t.label}
        </button>
      ))}
    </div>
  );
}
/** La salida, anclada al pie de la nav. */
export function AccountOut({ children, ...rest }: HTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className="sk-nav-item sk-account-out" {...rest}><Icon name="logout" />{children}</button>;
}
export function AccountFoot({ children }: { children: ReactNode }) {
  return <div className="sk-account-foot">{children}</div>;
}
/** El cuerpo con scroll propio; la pestaña abierta vive dentro. */
export function AccountBody({ busy, children, bodyRef }: { busy?: boolean | undefined; children: ReactNode; bodyRef?: React.Ref<HTMLDivElement> | undefined }) {
  return <div className="sk-account-body" ref={bodyRef} aria-busy={busy === true ? true : undefined}>{children}</div>;
}
export function AccountClose({ onClick, label }: { onClick: () => void; label: string }) {
  return <button type="button" className="sk-btn sk-btn--icon sk-btn--s sk-account-close" aria-label={label} onClick={onClick}>✕</button>;
}
export function AccountTitle({ id, children }: { id: string; children: ReactNode }) {
  return <h2 className="sk-account-t" id={id}>{children}</h2>;
}
/** Subtítulo de sección (el mismo de la modal del sistema). */
export function ModalSub({ children, style }: { children: ReactNode; style?: CSSProperties | undefined }) {
  return <p className="sk-modal-sub" style={style}>{children}</p>;
}
/** Rótulo de subsección dentro de una pestaña («Formas de entrar»). */
/** `as="h3"` + `id`: el rótulo TITULA una sección de la pestaña (la nombra con `aria-labelledby`); `anchor`, foco programático. */
export function AccountSection({ children, as: Tag = "span", id, anchor }: { children: ReactNode; as?: "span" | "h3" | undefined; id?: string | undefined; anchor?: boolean | undefined }) {
  return <Tag className="sk-overline sk-account-sec" id={id} tabIndex={anchor === true ? -1 : undefined}>{children}</Tag>;
}
/** Subtítulo DENTRO de un bloque de la pestaña (`h4.sk-label`): «Tres filas, tal como las leo» en la tarjeta de una hoja. */
export function AccountSubhead({ id, children, className }: { id?: string | undefined; children: ReactNode; className?: string | undefined }) {
  return <h4 className={cx("sk-label", className)} id={id}>{children}</h4>;
}
/** Panel de una pestaña. */
export function AccountPanel({ id, hidden, children }: { id: string; hidden?: boolean | undefined; children: ReactNode }) {
  return <section id={`p-${id}`} role="tabpanel" aria-labelledby={`t-${id}`} tabIndex={0} hidden={hidden}>{children}</section>;
}

/* ── filas ──────────────────────────────────────────────────────────────── */

/** Fila de ajuste: etiqueta + glosa a la izquierda, valor o mando a la derecha. */
export function SetRow({ label, hint, top, children, style }: { label: ReactNode; hint?: ReactNode | undefined; top?: boolean | undefined; children?: ReactNode | undefined; style?: CSSProperties | undefined }) {
  return (
    <div className={cx("sk-setrow", top === true && "sk-setrow--top")} style={style}>
      <div className="sk-setrow-l"><b>{label}</b>{hint !== undefined && <span>{hint}</span>}</div>
      {children !== undefined && <div className="sk-setrow-v">{children}</div>}
    </div>
  );
}
/** Fila de lista con icono o avatar: aparato, persona del equipo. */
export function ItemRow({ icon, avatar, title, meta, act, gone, variant, extra }: {
  icon?: IconName | undefined; avatar?: ReactNode | undefined; title: ReactNode; meta: ReactNode; act?: ReactNode | undefined; gone?: boolean | undefined;
  /** Lo que va bajo la meta, dentro del cuerpo: la cobertura de una hoja fijada (`CoverList`). */
  extra?: ReactNode | undefined;
  /** `ref`: fila de REFERENCIA (una fuente en un panel estrecho): el título a todo el ancho y el acto debajo. */
  variant?: "ref" | undefined;
}) {
  return (
    <div className={cx("sk-itemrow", variant === "ref" && "sk-itemrow--ref", gone === true && "sk-gone")}>
      {icon !== undefined && <div className="sk-itemrow-ic"><Icon name={icon} /></div>}
      {avatar}
      <div className="sk-itemrow-b"><b>{title}</b><span>{meta}</span>{extra}</div>
      {act !== undefined && <span className="sk-itemrow-act">{act}</span>}
    </div>
  );
}
/** Fila de factura: mes, importe, estado y la descarga. */
export function InvRow({ month, amount, status, act }: { month: ReactNode; amount: ReactNode; status: ReactNode; act?: ReactNode | undefined }) {
  return (
    <div className="sk-invrow">
      <span className="sk-invrow-m">{month}</span>
      <span className="sk-num">{amount}</span>
      <span className="sk-invrow-st">{status}</span>
      {act !== undefined && <span className="sk-invrow-act">{act}</span>}
    </div>
  );
}

/* ── plan, zona delicada y lo irreversible ──────────────────────────────── */

export function PlanName({ children }: { children: ReactNode }) {
  return <div className="sk-plan-n">{children}</div>;
}
export function PlanPrice({ children }: { children: ReactNode }) {
  return <div className="sk-plan-p">{children}</div>;
}
export function PlanFeats({ items }: { items: ReadonlyArray<ReactNode> }) {
  return <ul className="sk-plan-feats">{items.map((f, i) => <li key={i}>{f}</li>)}</ul>;
}
/** Zona delicada: lo que no se deshace, separado del resto. */
export function DangerZone({ title, children }: { title: ReactNode; children: ReactNode }) {
  return <div className="sk-dangerzone"><span className="sk-overline">{title}</span>{children}</div>;
}
/** Lista de consecuencias de un acto irreversible (`warn` = la que frena). */
export function Conseq({ items }: { items: ReadonlyArray<{ text: ReactNode; warn?: boolean }> }) {
  return <ul className="sk-conseq">{items.map((it, i) => <li key={i} className={cx(it.warn === true && "sk-warn-item")}>{it.text}</li>)}</ul>;
}
/**
 * Mantener pulsado: nada irreversible se dispara con un clic. Al completar el
 * gesto llama a `onHold`; soltar antes lo cancela y vuelve a cero.
 * Tecnologías de apoyo: un lector de pantalla o un teclado virtual activan
 * con un clic SINTÉTICO (`detail === 0`, sin pulsación de ratón ni tecla
 * previa), y no pueden mantener: ese clic completa el acto. El teclado físico
 * no llega aquí (Enter y Espacio cancelan su clic en `keydown`). La pantalla
 * enlaza una instrucción visible que lo dice (`describedBy`).
 */
export function Hold({ label, done, ms = 900, onHold, blocked, onBlocked, describedBy, stale, id, antes }: {
  label: string; done?: string | undefined; ms?: number | undefined; onHold: () => void;
  /** Apagado CON PORQUÉ (`aria-disabled`, sigue alcanzable): no arranca y,
      al intentarlo, llama a `onBlocked` para que la pantalla diga el motivo
      (que además enlaza `describedBy`). La firma de una ficha cuya vista
      previa no se puede firmar (ficha.html). */
  blocked?: boolean | undefined; onBlocked?: (() => void) | undefined; describedBy?: string | undefined;
  /** Sin conexión: además fuera del teclado (`tabindex=-1`). */
  stale?: boolean | undefined; id?: string | undefined;
  /** Comprobación al EMPEZAR (encendido): `false` no arranca — el formulario
      dice ahí mismo qué falta (una respuesta en blanco), sin hacer esperar
      el gesto entero para un no. */
  antes?: (() => boolean) | undefined;
}) {
  const [p, setP] = useState(0);
  const [hecho, setHecho] = useState(false);
  const raf = useRef(0);
  const corriendo = useRef(false);
  const fin = useRef(onHold);
  fin.current = onHold;
  const parar = useCallback(() => {
    if (!corriendo.current) return;
    corriendo.current = false;
    cancelAnimationFrame(raf.current);
    if (!hecho) setP(0);
  }, [hecho]);
  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  const paso = (t0: number): void => {
    const v = Math.min((performance.now() - t0) / ms, 1);
    setP(v);
    if (v >= 1) {
      corriendo.current = false; cancelAnimationFrame(raf.current);
      // con `done`, el mando se queda «hecho»; sin él, se rearma: si el acto
      // falla (un 409, la red), se puede volver a mantener (la firma de una ficha)
      if (done === undefined) setP(0);
      completar();
    }
    else raf.current = requestAnimationFrame(() => paso(t0));
  };
  const apagado = blocked === true || stale === true;
  const completar = (): void => {
    if (done !== undefined) setHecho(true);
    fin.current();
  };
  const empezar = (): void => {
    if (apagado) { onBlocked?.(); return; }
    if (corriendo.current || hecho) return;
    if (antes !== undefined && !antes()) return;
    corriendo.current = true; paso(performance.now());
  };
  return (
    <button
      type="button" className="sk-hold" id={id}
      aria-disabled={apagado || undefined} aria-describedby={describedBy}
      tabIndex={stale === true ? -1 : undefined}
      onPointerDown={(e) => { e.preventDefault(); empezar(); }}
      onPointerUp={parar} onPointerLeave={parar} onBlur={parar}
      // con el teclado también hay que MANTENER: la tecla arranca (y el
      // autorrepetido no reinicia) y soltarla cancela. Un Enter suelto no
      // puede disparar lo irreversible, que es para lo que existe la pieza.
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!(apagado && e.repeat)) empezar(); } }}
      onKeyUp={(e) => { if (e.key === "Enter" || e.key === " ") parar(); }}
      onClick={(e) => {
        if (e.detail !== 0 || corriendo.current) return;
        if (apagado) { onBlocked?.(); return; }
        if (hecho || (antes !== undefined && !antes())) return;
        completar();
      }}
    >
      <span className="sk-hold-fill" style={{ width: `${p * 100}%` }} />
      <span>{hecho && done !== undefined ? done : label}</span>
    </button>
  );
}
/** Diálogo POR ENCIMA de la cuenta: lo irreversible no se decide en su sitio. */
export function OverModal({ title, subtitle, onClose, children, footer }: { title: ReactNode; subtitle?: ReactNode | undefined; onClose: () => void; children: ReactNode; footer: ReactNode }) {
  const caja = useRef<HTMLDivElement>(null);
  useDialogKeys(caja, true, onClose);
  const id = "over-t";
  return (
    <>
      <div className="sk-scrim sk-scrim--over sk-open" onClick={onClose} />
      <div className="sk-modal-layer sk-modal-layer--over sk-open">
        <div ref={caja} className="sk-modal" role="dialog" aria-modal="true" aria-labelledby={id}>
          <h2 className="sk-h2" id={id}>{title}</h2>
          {subtitle !== undefined && <p className="sk-modal-sub">{subtitle}</p>}
          {children}
          <div className="sk-modal-foot">{footer}</div>
        </div>
      </div>
    </>
  );
}

/* ── controles ──────────────────────────────────────────────────────────── */

/** Grupo de radios del sistema (la elección de marca de Preferencias). */
export function RadioGroup({ children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className="sk-radiogroup" {...rest}>{children}</div>;
}
/** Área de texto del sistema (corregir un hecho, contar cómo fue). `mono`:
    la variante de código (`sk-textarea--mono`), para editar una receta YAML
    donde la sangría es sintaxis y una fuente proporcional la esconde. */
export function Textarea({ mono, className, ...rest }: { mono?: boolean | undefined; ref?: React.Ref<HTMLTextAreaElement> | undefined } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx("sk-textarea", mono === true && "sk-textarea--mono", className)} {...rest} />;
}
